import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Truck, Flame, Award, ShoppingCart } from 'lucide-react';

export default function ProcessSection() {
  const { t } = useLanguage();
  const ref = useScrollReveal();

  const steps = [
    { icon: Truck, num: '01', title: t('process.step1_title'), desc: t('process.step1_desc') },
    { icon: Flame, num: '02', title: t('process.step2_title'), desc: t('process.step2_desc') },
    { icon: Award, num: '03', title: t('process.step3_title'), desc: t('process.step3_desc') },
    { icon: ShoppingCart, num: '04', title: t('process.step4_title'), desc: t('process.step4_desc') },
  ];

  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1E2E] via-[#0E2538] to-[#0B1E2E]" />

      <div ref={ref} className="reveal relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('process.caption')}
        </p>
        <div className="section-line mb-8" />

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-16 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('process.title')}
        </h2>

        {/* Process steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} data-testid={`process-step-${i}`} className="relative group">
                {/* Connector line (desktop) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-[calc(50%+24px)] right-[-2rem] h-[2px] bg-gradient-to-r from-[#42D2D9]/40 to-transparent" />
                )}

                <div className="glass-card p-8 rounded-lg h-full">
                  {/* Step number */}
                  <span className="text-xs text-[#42D2D9]/50 font-bold tracking-widest mb-4 block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.num}
                  </span>

                  <div className="w-12 h-12 rounded-lg bg-[#42D2D9]/10 flex items-center justify-center mb-6 group-hover:bg-[#42D2D9]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#42D2D9]" strokeWidth={1.5} />
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Partner badge — Corsair official */}
        <div className="mt-20 glass-card rounded-lg p-8 md:p-12" data-testid="corsair-partner-badge">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 justify-center">
            {/* Corsair logo */}
            <div className="flex items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_plastic-wealth/artifacts/zuznhpmw_image.png"
                alt="Corsair Group"
                className="h-20 w-20 md:h-24 md:w-24 rounded-xl"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_plastic-wealth/artifacts/e61n26u7_image.png"
                alt="Corsair - For a Cleaner Future"
                className="h-20 md:h-24 object-contain rounded-lg"
              />
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px h-16 bg-white/10" />
            <div className="md:hidden w-32 h-px bg-white/10" />

            {/* Text */}
            <div className="text-center md:text-left">
              <p className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('process.official_rep')}
              </p>
              <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
                {t('process.partner_desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
